import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import {
  CreateGeneratorStatusManifestCorrectionsInput,
  MarkGeneratorStatusExceedancesResolvedMutationVariables,
} from "generated-graphql/graphql";

import { EXCEEDANCE_REFETCH } from "./exceedanceKeys";

const MARK_RESOLVED_MUTATION = gql(`
  mutation MarkGeneratorStatusExceedancesResolved(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
    $submissionId: ID
    $manifestDiscrepancyIds: [ID!]
  ) {
    markGeneratorStatusExceedancesResolved(
      exceedances: $exceedances
      note: $note
      submissionId: $submissionId
      manifestDiscrepancyIds: $manifestDiscrepancyIds
    ) {
      id
      status
    }
  }
`);

const CREATE_MANIFEST_CORRECTION_MUTATION = gql(`
  mutation CreateGeneratorStatusManifestCorrections($input: CreateGeneratorStatusManifestCorrectionsInput!) {
    createGeneratorStatusManifestCorrections(input: $input) { id }
  }
`);

/** Refresh both review surfaces before closing the resolution dialog. */
export function useResolutionMutations({
  onResolved,
  onCreated,
}: {
  onResolved: () => void;
  onCreated: () => void;
}) {
  const alerts = useAlerts();
  const [markResolved, { loading: resolving }] = useMutation(MARK_RESOLVED_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });
  const [createCorrection, { loading: creating }] = useMutation(
    CREATE_MANIFEST_CORRECTION_MUTATION,
    {
      refetchQueries: [
        ...EXCEEDANCE_REFETCH,
        "GeneratorStatusExceedanceLinkableManifestCorrections",
      ],
      awaitRefetchQueries: true,
    }
  );

  const resolve = async (
    variables: MarkGeneratorStatusExceedancesResolvedMutationVariables,
    successMessage: string
  ) => {
    try {
      await markResolved({ variables });
      alerts.success(successMessage);
      onResolved();
    } catch (e) {
      alerts.error("Failed to mark resolved.", e);
    }
  };

  const create = async (input: CreateGeneratorStatusManifestCorrectionsInput) => {
    try {
      await createCorrection({ variables: { input } });
      alerts.success(
        "Corrections created and linked. Mark each fixed under Manifest corrections once the manifest is corrected."
      );
      onCreated();
    } catch (e) {
      alerts.error("Failed to create the corrections.", e);
    }
  };

  return { resolve, create, submitting: resolving || creating };
}
